import _ from 'lodash';
import React from 'react';
import {connect} from 'react-redux';
import {reduxForm, stopSubmit, formValueSelector} from 'redux-form';

import moment from 'moment';
import classNames from 'classnames';

import ErrorBlock from 'components/ErrorBlock';
import BookingInformation from 'components/BookingInformation';
import Warning from 'components/Warning';
import {BaseFields, ItemFields} from './components';

import {formatDate, DATE_FORMAT} from 'utils/datetime';
import {clearField} from 'utils/forms';
import {
  MODALS,
  SCHEDULE_ITEM_TYPES,
  FETCH_SCHEDULE_DELAY,
  getNextFreeIdCounter,
  fetchXeroAccounts,
  fetchXeroTaxRates,
  createInvoice,
  fetchCanCreateFinanceItemForPastMonths
} from 'ducks/tenancy';
import {delayedAction, composeActions} from 'ducks/common';

import {prepareFormData} from './utils';
import {selectModal, storeDataInModal} from 'ducks/modals';

import './styles.css';

const ID_COUNTER_ERROR =
  'Invoice with this Id counter and Id string prefix already exists.';
const ID_COUNTER_WARNING_MESSAGE =
  "Sorry, 'INVOICE NO' was already taken! A new one was refetched. Please, submit again!";

const isIdCounterError = errors =>
  _.get(errors, '_error[0]') === ID_COUNTER_ERROR;

const formSubmit = (values, dispatch, props) => {
  const scheduleType = props.scheduleType || props.selectedScheduleType;
  const postData = prepareFormData(
    values,
    props.locationRef,
    props.tenancyId,
    scheduleType
  );

  /*
   * If the invoice number is alredy taken we need to refetch new one from the server.
   * When do that we don't display errors for the invoice number field but a warning
   * since calling setting the fetched number hides the errors that are displayed on stopsubmit.
   */
  const nextFreeIdCounterExtraActions = [
    ({idCounter}) => props.change('idCounter', idCounter),
    () =>
      props.storeDataInModal({
        modalName: MODALS.INVOICE_CREATE,
        data: {
          idCounterWarningMessage: ID_COUNTER_WARNING_MESSAGE
        }
      })
  ];

  const refetchIdCounterExtraAction = ({errors}) =>
    isIdCounterError(errors)
      ? props.getNextFreeIdCounter(
          {
            tenancyId: props.tenancyId,
            objectType: SCHEDULE_ITEM_TYPES.INVOICE
          },
          nextFreeIdCounterExtraActions
        )
      : dispatch(stopSubmit('invoiceCreate', errors));

  const failureExtraActions = [
    refetchIdCounterExtraAction,
    ({errors}) =>
      isIdCounterError(errors)
        ? dispatch(stopSubmit('invoiceCreate'))
        : dispatch(stopSubmit('invoiceCreate', errors))
  ];

  const successExtraActions = props.getExtraActions({scheduleType});
  const delayedExtraActions = props.delayExtraActions({
    tenancyId: props.tenancyId,
    scheduleType
  });

  if (!_.isEmpty(delayedExtraActions)) {
    const delayedFetchSchedule = () =>
      delayedAction({
        delay: FETCH_SCHEDULE_DELAY,
        action: () => composeActions(delayedExtraActions)
      });
    successExtraActions.push(delayedFetchSchedule);
  }

  dispatch(createInvoice(postData, successExtraActions, failureExtraActions));
};

class InvoiceCreateForm extends React.Component<any> {
  componentDidMount() {
    const {tenancyId} = this.props;
    const getNextFreeIdCounterExtraAction = this.props.storeDataExtraActions(
      ({idCounter}) => ({idCounter})
    );

    this.props.getNextFreeIdCounter(
      {
        tenancyId,
        objectType: SCHEDULE_ITEM_TYPES.INVOICE
      },
      getNextFreeIdCounterExtraAction
    );

    const fetchXeroAccountsExtraAction = this.props.storeDataExtraActions(
      data => ({
        xeroAccounts: data
      })
    );
    this.props.fetchXeroAccounts(
      {location: this.props.locationRef},
      fetchXeroAccountsExtraAction
    );
    const fetchCanCreateFinanceItemForPastMonthsExtraAction = this.props.storeDataExtraActions(
      data => ({
        canCreateInvoiceForPastMonths: _.get(
          data,
          'canCreateFinanceItemsForPastMonths'
        )
      })
    );

    this.props.fetchCanCreateFinanceItemForPastMonths(
      {locationRef: this.props.locationRef},
      fetchCanCreateFinanceItemForPastMonthsExtraAction
    );
  }

  componentDidUpdate(prevProps) {
    const accountsFetched =
      _.get(this.props, 'xeroAccounts') !== _.get(prevProps, 'xeroAccounts');

    if (accountsFetched && !_.isEmpty(_.get(this.props, 'xeroAccounts'))) {
      const accountClass = _.get(this.props.xeroAccounts, '[0].accountClass');
      this.fetchXeroTaxRates({accountClass});
    }
  }

  fetchXeroTaxRates = filters => {
    const fetchXeroTaxRatesExtraAction = this.props.storeDataExtraActions(
      data => ({
        xeroTaxRates: data
      })
    );
    this.props.fetchXeroTaxRates(
      {filters: this.buildXeroTaxRatesFilter(filters)},
      fetchXeroTaxRatesExtraAction
    );
    this.clearTaxRateValue();
  };

  buildXeroTaxRatesFilter = ({accountClass}) => {
    const {locationRef} = this.props;

    return {
      accountClass: accountClass,
      location: locationRef
    };
  };

  clearTaxRateValue = () => {
    clearField(this, {field: 'taxRate'});
  };

  render() {
    const {
      className,
      handleSubmit,
      error,
      openFromBookingSet,
      booking,
      selectedScheduleType,
      canCreateInvoiceForPastMonths,
      selectedDueDate
    } = this.props;

    const draftInvoiceCreationThreshold = moment().add(42, 'days');
    const dueDateIsAfterThreshold = moment(
      selectedDueDate,
      DATE_FORMAT
    ).isAfter(draftInvoiceCreationThreshold);

    return (
      <div>
        {error && <ErrorBlock>{error}</ErrorBlock>}
        {dueDateIsAfterThreshold && (
          <Warning>
            The selected due date is too far in the future. Consider creating a
            draft invoice instead.
          </Warning>
        )}
        <form
          onSubmit={handleSubmit}
          className={classNames(className, 'InvoiceCreateForm')}>
          {openFromBookingSet && <BookingInformation booking={booking} />}
          <BaseFields
            xeroAccounts={this.props.xeroAccounts || []}
            scheduleType={this.props.scheduleType || selectedScheduleType}
            fetchXeroTaxRates={this.fetchXeroTaxRates}
            openFromBookingSet={openFromBookingSet}
            canCreateInvoiceForPastMonths={canCreateInvoiceForPastMonths}
          />
          <ItemFields
            lineAmount={this.props.lineAmount}
            xeroTaxRates={this.props.xeroTaxRates || []}
          />
        </form>
      </div>
    );
  }
}

const formSelector = formValueSelector('invoiceCreate');

const mapStateToProps = state => {
  const {
    data: {idCounter, xeroAccounts, xeroTaxRates, canCreateInvoiceForPastMonths}
  } = selectModal(state, MODALS.INVOICE_CREATE);

  let account: any = null;
  if (!_.isEmpty(xeroAccounts)) {
    account = _.first(xeroAccounts);
    account = {
      value: account.id,
      label: `${account.code} - ${account.name}`,
      accountClass: account.accountClass,
      id: account.id
    };
  }

  let taxRate: any = null;
  if (xeroTaxRates) {
    taxRate = _.first(xeroTaxRates);
    taxRate = {
      value: taxRate.id,
      label: taxRate.name,
      effectiveRate: taxRate.effectiveRate
    };
  }

  const today = formatDate(moment());

  return {
    xeroAccounts,
    xeroTaxRates,
    initialValues: {
      account,
      taxRate,
      idCounter,
      discount: '0',
      quantity: '1',
      dateIssued: today,
      dueDate: today
    },
    selectedScheduleType: _.get(formSelector(state, 'scheduleType'), 'value'),
    canCreateInvoiceForPastMonths,
    selectedDueDate: formSelector(state, 'dueDate')
  };
};

const mapDispatchToProps = {
  getNextFreeIdCounter,
  fetchXeroAccounts,
  fetchXeroTaxRates,
  createInvoice,
  storeDataInModal,
  fetchCanCreateFinanceItemForPastMonths
};

export default connect(mapStateToProps, mapDispatchToProps)(
  reduxForm({
    form: 'invoiceCreate',
    onSubmit: formSubmit,
    enableReinitialize: true
  })(InvoiceCreateForm)
);
